import React, { useState, useEffect } from 'react';
import { MoreHorizontal, Scale, Briefcase, Gavel, Calculator, Landmark, Compass, X, Users, MessageCircle } from 'lucide-react';
import AddPartnerModal from './AddPartnerModal';
import AdvisoryMessageModal from './AdvisoryMessageModal';
import StartDealTeamModal from '../DealTeam/StartDealTeamModal';
import AdvisorCard from '../Sponsorship/AdvisorCard';
import NetworkSuggestion from '../Sponsorship/NetworkSuggestion';

/* ─── Constants ────────────────────────────────────────────────────── */

const ROLE_CONFIG = {
  cbv: { name: 'Valuation Specialist (CBV)', icon: Scale, description: "Determines your business's fair market value" },
  business_broker: { name: 'Business Broker / M&A Advisor', icon: Briefcase, description: 'Finds buyers and negotiates the sale' },
  lawyer: { name: 'Lawyer', icon: Gavel, description: 'Handles legal structure, contracts, and due diligence' },
  accountant: { name: 'Accountant / Tax Advisor', icon: Calculator, description: 'Manages tax planning and financial clean-up' },
  lender: { name: 'Lender / Financing', icon: Landmark, description: 'Structures acquisition financing for buyers' },
  cepa_coach: { name: 'CEPA / Exit Planner', icon: Compass, description: 'Coaches you through exit readiness and value acceleration' },
};

const ALL_ROLES = ['cbv', 'business_broker', 'lawyer', 'accountant', 'lender', 'cepa_coach'];

/* ─── Helpers ──────────────────────────────────────────────────────── */

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function getStatusConfig(status) {
  switch (status) {
    case 'active':
      return {
        label: 'Active',
        color: 'text-emerald-500 border-emerald-500/30 bg-emerald-500/10',
        borderColor: 'border-l-emerald-500',
      };
    case 'invited':
      return {
        label: 'Invited',
        color: 'text-[hsl(var(--succeedo-orange))] border-[hsl(var(--succeedo-orange))]/30 bg-[hsl(var(--succeedo-orange))]/10',
        borderColor: 'border-l-[hsl(var(--succeedo-orange))]',
      };
    case 'added':
      return {
        label: 'Added',
        color: 'text-blue-500 border-blue-500/30 bg-blue-500/10',
        borderColor: 'border-l-blue-500',
      };
    case 'off_platform':
      return {
        label: 'Off-platform',
        color: 'text-[hsl(var(--muted-foreground))] border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30',
        borderColor: 'border-l-[hsl(var(--muted-foreground))]',
      };
    case 'exploring_options':
      return {
        label: 'Exploring options',
        color: 'text-amber-500 border-amber-500/30 bg-amber-500/10',
        borderColor: 'border-l-amber-500',
      };
    default:
      return {
        label: status || 'Unknown',
        color: 'text-[hsl(var(--muted-foreground))] border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30',
        borderColor: 'border-l-[hsl(var(--border))]',
      };
  }
}

/* ─── TeamBuilderPage Component ───────────────────────────────────── */

const TeamBuilderPage = ({ advisoryTeamMembers = [], partners = [], userProvince, userIndustry, ownerCompanyName, existingDealTeam = null, onAdd, onUpdate, onRemove, onRequestIntroduction, onCreateDealTeam, loading, sponsorship = null, onAddNetwork, onRequestAdvisor, onRemoveAdvisor }) => {
  const [isAddPartnerOpen, setIsAddPartnerOpen] = useState(false);
  const [modalDefaultTab, setModalDefaultTab] = useState('existing');
  const [modalDefaultRole, setModalDefaultRole] = useState(null);
  const [modalReplaceTargetId, setModalReplaceTargetId] = useState(null);
  const [showEducationalHeader, setShowEducationalHeader] = useState(true);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [isStartDealTeamOpen, setIsStartDealTeamOpen] = useState(false);
  const [messageModalMember, setMessageModalMember] = useState(null);

  // Track page view
  useEffect(() => {
    if (window.ahoy) {
      window.ahoy.track('succeedo_team_page_viewed');
    }
  }, []);

  // Load educational header preference
  useEffect(() => {
    const dismissed = localStorage.getItem('team-hub-educational-dismissed');
    if (dismissed === 'true') {
      setShowEducationalHeader(false);
    }
  }, []);

  // Close menu on outside click
  useEffect(() => {
    if (!openMenuId) return;
    const handleClick = () => setOpenMenuId(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [openMenuId]);

  const openModal = (tab = 'existing', role = null, replaceTargetId = null) => {
    setModalDefaultTab(tab);
    setModalDefaultRole(role);
    setModalReplaceTargetId(replaceTargetId);
    setIsAddPartnerOpen(true);

    if (window.ahoy) {
      window.ahoy.track('succeedo_team_modal_opened', { tab, role, replace: !!replaceTargetId });
    }
  };

  const handleDismissEducational = () => {
    localStorage.setItem('team-hub-educational-dismissed', 'true');
    setShowEducationalHeader(false);
  };

  const handleRemove = async (member) => {
    if (!window.confirm(`Remove ${member.name || 'this team member'} from your team?`)) return;
    try {
      await onRemove({ id: member.id });
      if (window.ahoy) {
        window.ahoy.track('succeedo_team_member_removed', { role: member.role });
      }
    } catch (err) {
      console.error('Failed to remove team member:', err);
    }
  };

  // Map team members to roles
  const getRoleMember = (roleId) => {
    return advisoryTeamMembers.find((m) => m.role === roleId);
  };

  const filledRoles = advisoryTeamMembers.length;
  const emptyRoles = ALL_ROLES.length - filledRoles;
  const emptyRoleIds = ALL_ROLES.filter((r) => !getRoleMember(r));

  // Threshold lowered from 2 to 1 — a focused workspace doesn't have to
  // be the full team. Exploring financing with just a lender (1 advisor)
  // is a legitimate use case; the seller adds more later if needed.
  const canStartDealTeam = filledRoles >= 1;
  const startDealTeamTooltip = canStartDealTeam
    ? 'Open a shared workspace with your team'
    : 'Add at least one advisor to your roster to start a shared workspace.';

  const handleOpenStartDealTeam = () => {
    if (!canStartDealTeam) return;
    setIsStartDealTeamOpen(true);
    if (window.ahoy) {
      window.ahoy.track('succeedo_workspace_modal_opened', { filled_roles: filledRoles });
    }
  };

  const handleStartDealTeamCreate = async ({ name, advisoryTeamMemberIds, additionalEmailInvites }) => {
    if (!onCreateDealTeam) return;
    await onCreateDealTeam({ name, advisoryTeamMemberIds, additionalEmailInvites });
  };

  const handleFindPartnerFromModal = (roleId) => {
    // Modal closes itself before invoking this. Open the AddPartnerModal in
    // the directory tab pre-filtered to the requested role — same plumbing
    // the role-card "Find one" button uses.
    openModal('directory', roleId);
  };

  return (
    <>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[hsl(var(--succeedo-orange))] shadow-[0_0_8px_hsl(var(--succeedo-orange))]" />
              <span className="font-mono text-[11px] text-[hsl(var(--muted-foreground))] uppercase tracking-wide">
                YOUR TEAM
              </span>
            </div>
            <h1 className="font-serif text-[42px] font-light text-[hsl(var(--foreground))] tracking-[-0.02em] leading-none">
              Your Team
            </h1>
          </div>
          <button
            onClick={() => openModal('existing', null)}
            className="bg-[hsl(var(--succeedo-orange))] text-white rounded-full px-5 py-2 text-[14px] font-medium hover:opacity-90 transition-opacity"
          >
            + Add Partner
          </button>
        </div>

        {/* Phase 5: the owner's advisor, or the offer to ask for one */}
        <AdvisorCard sponsorship={sponsorship} onRequestAdvisor={onRequestAdvisor} onRemoveAdvisor={onRemoveAdvisor} />

        {/* Educational Header (dismissable) */}
        {showEducationalHeader && (
          <div className="bg-[hsl(var(--muted))]/30 border border-[hsl(var(--border))] rounded-[16px] p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-2">
                <h2 className="text-[16px] font-semibold text-[hsl(var(--foreground))]">
                  Why your team matters
                </h2>
                <p className="text-[14px] text-[hsl(var(--muted-foreground))] leading-relaxed">
                  A successful business transition is a team effort. Most owners need 4-6 professionals — each handling a different part of the process. Start by telling us who you already work with, and we&apos;ll help fill the gaps.
                </p>
              </div>
              <button
                onClick={handleDismissEducational}
                className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <button
              onClick={handleDismissEducational}
              className="mt-4 border border-[hsl(var(--border))] text-[hsl(var(--foreground))] rounded-full px-4 py-2 text-[13px] hover:bg-[hsl(var(--accent))] transition-colors"
            >
              Got it
            </button>
          </div>
        )}

        {/* Shared-workspace CTA — purpose-scoped, not deal-scoped. The
            same backend (DealTeam model) powers it; the framing is
            broader so it covers financing exploration / succession
            planning / active sale / other. The purpose picker lives
            in the StartDealTeamModal. */}
        <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] border-l-2 border-l-[hsl(var(--succeedo-orange))] rounded-[16px] p-5 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[hsl(var(--succeedo-orange))]/10 flex items-center justify-center shrink-0">
              <Users className="w-5 h-5 text-[hsl(var(--succeedo-orange))]" />
            </div>
            <div className="space-y-1">
              <h3 className="text-[15px] font-semibold text-[hsl(var(--foreground))]">
                {existingDealTeam ? 'Your shared workspace is ready' : 'Need a shared workspace?'}
              </h3>
              <p className="text-[13px] text-[hsl(var(--muted-foreground))]">
                {existingDealTeam
                  ? `Continue sharing documents, messaging the group, and tracking milestones for ${existingDealTeam.name}.`
                  : 'Bring your team into a focused space for a sale, financing exploration, succession planning, or anything you want to coordinate on. One active workspace at a time.'}
              </p>
            </div>
          </div>
          {existingDealTeam ? (
            <a
              href={`/succeedo/teams/${existingDealTeam.id}/workspace`}
              aria-label="Open shared workspace"
              className="shrink-0 rounded-full px-5 py-2 text-[14px] font-medium transition-opacity whitespace-nowrap bg-[hsl(var(--succeedo-orange))] text-white hover:opacity-90"
            >
              Open Workspace
            </a>
          ) : (
            <button
              onClick={handleOpenStartDealTeam}
              disabled={!canStartDealTeam}
              title={startDealTeamTooltip}
              aria-label="Start a shared workspace"
              className={`shrink-0 rounded-full px-5 py-2 text-[14px] font-medium transition-opacity whitespace-nowrap ${
                canStartDealTeam
                  ? 'bg-[hsl(var(--succeedo-orange))] text-white hover:opacity-90'
                  : 'bg-[hsl(var(--succeedo-orange))]/40 text-white opacity-60 cursor-not-allowed'
              }`}
            >
              Start a workspace
            </button>
          )}
        </div>

        {/* Role Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {ALL_ROLES.map((roleId) => {
            const member = getRoleMember(roleId);
            const roleConfig = ROLE_CONFIG[roleId];
            const RoleIcon = roleConfig.icon;
            const hasGap = !member;

            if (member) {
              // STATE A: FILLED CARD
              const statusConfig = getStatusConfig(member.status);
              const initials = getInitials(member.name);

              return (
                <div
                  key={roleId}
                  className={`bg-[hsl(var(--card))] border border-[hsl(var(--border))] border-l-2 ${statusConfig.borderColor} rounded-[16px] p-6 space-y-4 relative`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      {/* Avatar */}
                      <div className="w-12 h-12 rounded-full bg-[hsl(var(--muted))] flex items-center justify-center shrink-0">
                        <span className="text-[hsl(var(--foreground))] font-semibold text-[15px]">
                          {initials}
                        </span>
                      </div>

                      {/* Info */}
                      <div className="space-y-2">
                        <div className="space-y-1">
                          <h3 className="text-[15px] font-semibold text-[hsl(var(--foreground))]">
                            {member.name}
                          </h3>
                          {member.firm && (
                            <p className="text-[13px] text-[hsl(var(--muted-foreground))]">
                              {member.firm}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-[11px] uppercase rounded-full px-3 py-1 text-[hsl(var(--succeedo-orange))] border border-[hsl(var(--succeedo-orange))]/30 inline-block">
                            {roleConfig.name.split(' / ')[0].split(' (')[0]}
                          </span>
                          <span
                            className={`font-mono text-[10px] uppercase rounded-full px-2 py-0.5 border inline-block ${statusConfig.color}`}
                          >
                            {statusConfig.label}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMessageModalMember({ ...member, role_label: roleConfig.name });
                          if (window.ahoy) {
                            try { window.ahoy.track('succeedo_advisor_message_button_clicked', { role: member.role }); } catch (_e) {}
                          }
                        }}
                        className="text-[hsl(var(--muted-foreground))]/40 hover:text-[hsl(var(--succeedo-orange))] transition-colors"
                        aria-label={`Message ${member.name || 'advisor'}`}
                        title="Send a message"
                      >
                        <MessageCircle className="w-5 h-5" />
                      </button>

                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuId(openMenuId === member.id ? null : member.id);
                        }}
                        className="text-[hsl(var(--muted-foreground))]/30 hover:text-[hsl(var(--muted-foreground))] transition-colors"
                      >
                        <MoreHorizontal className="w-5 h-5" />
                      </button>
                      {openMenuId === member.id && (
                        <div className="absolute right-0 top-8 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-lg shadow-lg py-1 w-48 z-10">
                          <button
                            onClick={() => {
                              setOpenMenuId(null);
                              openModal('existing', roleId, member.id);
                            }}
                            className="w-full text-left px-4 py-2 text-[13px] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--accent))] transition-colors"
                          >
                            Replace
                          </button>
                          <button
                            onClick={() => {
                              setOpenMenuId(null);
                              handleRemove(member);
                            }}
                            className="w-full text-left px-4 py-2 text-[13px] text-[hsl(var(--destructive))] hover:bg-[hsl(var(--accent))] transition-colors"
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                    </div>
                  </div>
                </div>
              );
            } else {
              // STATE B: EMPTY CARD
              return (
                <div
                  key={roleId}
                  className="bg-[hsl(var(--card))] border-2 border-dashed border-[hsl(var(--border))] rounded-[16px] p-6 space-y-4 relative"
                >
                  <div className="flex flex-col items-center text-center space-y-4">
                    {/* Role Icon */}
                    <div className="w-12 h-12 rounded-full bg-[hsl(var(--muted))]/50 flex items-center justify-center">
                      <RoleIcon className="w-6 h-6 text-[hsl(var(--muted-foreground))]" />
                    </div>

                    {/* Role Info */}
                    <div className="space-y-1">
                      <h3 className="text-[15px] font-semibold text-[hsl(var(--foreground))]">
                        {roleConfig.name}
                      </h3>
                      <p className="text-[13px] text-[hsl(var(--muted-foreground))]">
                        {roleConfig.description}
                      </p>
                    </div>

                    {/* Phase 5: the advisor's network first */}
                    {sponsorship?.advisor && (sponsorship.network || []).filter((c) => c.category === roleId).slice(0, 1).map((contact) => (
                      <NetworkSuggestion key={contact.id} contact={contact} advisorName={sponsorship.advisor.name} onAdd={onAddNetwork} />
                    ))}

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 w-full">
                      <button
                        onClick={() => openModal('existing', roleId)}
                        className="flex-1 border border-[hsl(var(--border))] text-[hsl(var(--foreground))] rounded-full py-2 text-[13px] hover:bg-[hsl(var(--accent))] transition-colors"
                      >
                        I have someone
                      </button>
                      <button
                        onClick={() => openModal('directory', roleId)}
                        className="flex-1 bg-[hsl(var(--succeedo-orange))] text-white rounded-full py-2 text-[13px] font-medium hover:opacity-90 transition-opacity"
                      >
                        Find one
                      </button>
                    </div>
                  </div>
                </div>
              );
            }
          })}
        </div>

        {/* Assessment Insight Strip */}
        <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] border-l-2 border-l-[hsl(var(--succeedo-orange))] rounded-[16px] p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <span className="bg-[hsl(var(--succeedo-orange))]/10 border border-[hsl(var(--succeedo-orange))]/20 text-[hsl(var(--succeedo-orange))] font-mono text-[11px] uppercase px-3 py-1 rounded-full inline-block shrink-0">
                ASSESSMENT INSIGHT
              </span>
              <div className="space-y-1">
                <p className="text-[14px] text-[hsl(var(--foreground))]">
                  {emptyRoles > 0 ? (
                    <>
                      Your team has{' '}
                      <span className="font-semibold">
                        {emptyRoles} open role{emptyRoles > 1 ? 's' : ''}
                      </span>
                      . Business sales typically involve 4-6 professionals working together. Filling these gaps reduces deal risk and timeline.
                    </>
                  ) : (
                    <>Your team is complete. All 6 advisory roles are filled.</>
                  )}
                </p>
              </div>
            </div>
            {emptyRoles > 0 && (
              <button
                onClick={() => openModal('directory', null)}
                className="text-[14px] text-[hsl(var(--succeedo-orange))] hover:underline whitespace-nowrap shrink-0"
              >
                Add Partners &rarr;
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Add Partner Modal */}
      <AddPartnerModal
        isOpen={isAddPartnerOpen}
        onClose={() => setIsAddPartnerOpen(false)}
        onAdd={onAdd}
        onUpdate={onUpdate}
        onRequestIntroduction={onRequestIntroduction}
        defaultTab={modalDefaultTab}
        defaultRole={modalDefaultRole}
        defaultRegion={userProvince || ''}
        defaultIndustry={userIndustry || ''}
        partners={partners}
        replaceTargetId={modalReplaceTargetId}
      />

      {/* Start Deal Team Modal */}
      {isStartDealTeamOpen && (
        <StartDealTeamModal
          isOpen={isStartDealTeamOpen}
          onClose={() => setIsStartDealTeamOpen(false)}
          ownerCompanyName={ownerCompanyName}
          advisoryTeamMembers={advisoryTeamMembers}
          onCreate={handleStartDealTeamCreate}
          onFindPartner={handleFindPartnerFromModal}
        />
      )}

      {/* 1:1 Advisor Message Modal */}
      <AdvisoryMessageModal
        isOpen={!!messageModalMember}
        member={messageModalMember}
        onClose={() => setMessageModalMember(null)}
      />
    </>
  );
};

export default TeamBuilderPage;
